import { Link } from "react-router-dom";
import {
  ArrowRight, DraftingCompass, Building2, ClipboardList, Truck, Accessibility,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import { PROCESS_STEPS } from "@/data/site";

const SERVICE_CARDS = [
  {
    icon: DraftingCompass,
    title: "Building Design",
    text: "Our experienced team analyzes your space requirements and delivers innovative solutions that meet your design and budget expectations.",
  },
  {
    icon: Building2,
    title: "Architectural / Engineering",
    text: "Buildings constructed with the strongest technologies in wood, steel or concrete — engineered to meet or surpass all applicable building codes.",
  },
  {
    icon: ClipboardList,
    title: "Project Management",
    text: "Full project services from budget proposal through turn-key construction, on projects from small and simple to large and complex.",
  },
  {
    icon: Truck,
    title: "Delivery / Installation",
    text: "We work directly with the manufacturer throughout the build and closely manage delivery and set up with our expert installers.",
  },
  {
    icon: Accessibility,
    title: "Decks / Handicap Ramps / Skirting",
    text: "Complete site finish-out including decks, ADA-compliant handicap ramps, and skirting for a polished, accessible final product.",
  },
];

const Services = () => {
  return (
    <div data-testid="services-page">
      <section className="relative overflow-hidden bg-[#001529]">
        <img src="/images/setup-hero.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#001529] via-[#00203d]/85 to-[#00203d]/50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-12 lg:pt-12 lg:pb-16">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-500 mb-4">VBS Services</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight max-w-3xl">
              Turn-key services, start to finish
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
              VBS offers turn-key services — so you get a single partner from first sketch to final walkthrough.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pt-10 pb-16 sm:pt-12 sm:pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {SERVICE_CARDS.map((s, i) => (
            <Reveal key={s.title} delay={(i % 3) * 90}>
              <article
                data-testid={`service-card-${s.title.toLowerCase().replace(/[\s/]+/g, "-")}`}
                className="group h-full bg-white rounded-xl p-6 sm:p-8 border border-slate-100 shadow-sm hover:shadow-xl transition-shadow duration-300"
              >
                <div className="h-12 w-12 rounded-lg bg-[#00203d] group-hover:bg-amber-600 flex items-center justify-center transition-colors duration-300">
                  <s.icon className="h-6 w-6 text-amber-500 group-hover:text-white transition-colors duration-300" aria-hidden="true" />
                </div>
                <h2 className="mt-5 font-heading text-lg sm:text-xl font-bold text-slate-900">{s.title}</h2>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{s.text}</p>
              </article>
            </Reveal>
          ))}

          <Reveal delay={180}>
            <div className="h-full rounded-xl overflow-hidden relative min-h-[260px]">
              <img src="/images/ext-office-aluminum.jpg" alt="Office building with high rib aluminum exterior" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-[#001529]/70" />
              <div className="relative p-6 sm:p-8 flex flex-col justify-end h-full min-h-[260px]">
                <h2 className="font-heading text-xl sm:text-2xl font-bold text-white">One contract. One team.</h2>
                <p className="mt-3 text-sm text-slate-200 leading-relaxed">
                  Once a contract is completed, we work directly with the manufacturer throughout the build and
                  closely manage delivery and set up.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white border-y border-slate-100" aria-labelledby="setup-options-heading">
        <div className="pt-10 pb-16 sm:pt-12 sm:pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <Reveal className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600 mb-4">Installation Options</p>
            <h2 id="setup-options-heading" className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 gold-underline">
              Standard & ground level setups
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Reveal>
              <article data-testid="setup-standard-card" className="overflow-hidden rounded-xl border border-slate-100 shadow-sm bg-slate-50">
                <img src="/images/setup-standard.jpg" alt="Standard setup modular sales center" className="h-64 w-full object-cover" />
                <div className="p-6 sm:p-8">
                  <h3 className="font-heading text-xl font-bold text-slate-900">Standard Setup</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                    A proven installation method for temporary and permanent modular buildings — fast to deploy,
                    easy to expand or relocate as your needs change.
                  </p>
                </div>
              </article>
            </Reveal>
            <Reveal delay={120}>
              <article data-testid="setup-ground-card" className="overflow-hidden rounded-xl border border-slate-100 shadow-sm bg-slate-50">
                <img src="/images/setup-ground.jpg" alt="Ground level setup modular building" className="h-64 w-full object-cover" />
                <div className="p-6 sm:p-8">
                  <h3 className="font-heading text-xl font-bold text-slate-900">Ground Level Setup</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                    Ground Set buildings are a great option — set at grade for a permanent, site-built look with no
                    steps or ramps required at entries.
                  </p>
                </div>
              </article>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="pt-10 pb-16 sm:pt-12 sm:pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" aria-labelledby="services-process-heading">
        <Reveal className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600 mb-4">How It Works</p>
          <h2 id="services-process-heading" className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 gold-underline">
            The ground set process
          </h2>
        </Reveal>
        <ol className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {PROCESS_STEPS.map((s, i) => (
            <Reveal key={s.step} delay={i * 90}>
              <li data-testid={`services-process-step-${s.step}`} className="group">
                <div className="overflow-hidden rounded-lg">
                  <img src={s.img} alt={s.title} loading="lazy" className="h-44 w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="mt-4 flex items-start gap-3">
                  <span className="font-heading text-2xl font-extrabold text-amber-600 leading-none">
                    {String(s.step).padStart(2, "0")}
                  </span>
                  <p className="text-sm font-semibold text-slate-800 leading-snug">{s.title}</p>
                </div>
              </li>
            </Reveal>
          ))}
        </ol>
        <Reveal delay={200}>
          <Link
            to="/contact"
            data-testid="services-quote-button"
            className="mt-10 inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-bold px-7 py-3.5 rounded-md transition-colors duration-200"
          >
            Start your project <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
};

export default Services;
